/**
 * Expiring Contracts Filters Component
 * US-CONT-005: View Expiring Contracts Alert
 * Filter panel for search, urgency, contract type, etc.
 */

import { useState } from 'react';
import { Search, X, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { ContractFilters, ContractUrgency, ContractType } from '../types/expiringContracts.types';
import {
  URGENCY_FILTER_OPTIONS,
  CONTRACT_TYPE_OPTIONS,
  URGENCY_LABELS
} from '../constants/expiringContracts.constants';

interface ExpiringContractsFiltersProps {
  filters: ContractFilters;
  onFiltersChange: (updates: Partial<ContractFilters>) => void;
  onReset: () => void;
  hasActiveFilters: boolean;
  className?: string;
}

export function ExpiringContractsFilters({
  filters,
  onFiltersChange,
  onReset,
  hasActiveFilters,
  className
}: ExpiringContractsFiltersProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  function handleSearchChange(search: string) {
    onFiltersChange({ search });
  }

  function toggleUrgency(urgency: ContractUrgency) {
    const current = filters.urgency || [];
    const updated = current.includes(urgency)
      ? current.filter(u => u !== urgency)
      : [...current, urgency];
    onFiltersChange({ urgency: updated });
  }

  function toggleContractType(type: ContractType) {
    const current = filters.contractType || [];
    const updated = current.includes(type)
      ? current.filter(t => t !== type)
      : [...current, type];
    onFiltersChange({ contractType: updated });
  }

  function getActiveFilterCount(): number {
    let count = 0;
    if (filters.search) count++;
    if (filters.urgency && filters.urgency.length > 0) count += filters.urgency.length;
    if (filters.contractType && filters.contractType.length > 0) count += filters.contractType.length;
    return count;
  }

  const activeCount = getActiveFilterCount();

  return (
    <Card className={cn('p-4', className)}>
      {/* Search and Quick Filters */}
      <div className="space-y-4">
        {/* Search Bar */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by contract number or customer..."
              value={filters.search || ''}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-9"
            />
          </div>

          <Button
            variant="outline"
            size="icon"
            onClick={() => setIsExpanded(!isExpanded)}
            className={cn(isExpanded && 'bg-muted')}
          >
            <Filter className="h-4 w-4" />
            {activeCount > 0 && (
              <Badge variant="destructive" className="absolute -right-1 -top-1 h-5 w-5 rounded-full p-0 text-xs">
                {activeCount}
              </Badge>
            )}
          </Button>

          {hasActiveFilters && (
            <Button variant="ghost" size="icon" onClick={onReset}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Active Filter Tags */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2">
            {filters.urgency && filters.urgency.map((urgency) => (
              <Badge key={urgency} variant="secondary" className="gap-1">
                {URGENCY_LABELS[urgency]}
                <button
                  onClick={() => toggleUrgency(urgency)}
                  className="ml-1 hover:text-destructive"
                  aria-label={`Remove ${urgency} filter`}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}

            {filters.contractType && filters.contractType.map((type) => (
              <Badge key={type} variant="secondary" className="gap-1">
                {CONTRACT_TYPE_OPTIONS.find(opt => opt.value === type)?.label}
                <button
                  onClick={() => toggleContractType(type)}
                  className="ml-1 hover:text-destructive"
                  aria-label={`Remove ${type} filter`}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}

        {/* Expanded Filters */}
        {isExpanded && (
          <div className="grid gap-6 pt-4 border-t md:grid-cols-2">
            {/* Urgency Filters */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Urgency Level</Label>
              <div className="space-y-2">
                {URGENCY_FILTER_OPTIONS.map((option) => (
                  <div key={option.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={`urgency-${option.value}`}
                      checked={filters.urgency?.includes(option.value as ContractUrgency) || false}
                      onCheckedChange={() => toggleUrgency(option.value as ContractUrgency)}
                    />
                    <label
                      htmlFor={`urgency-${option.value}`}
                      className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      {option.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Contract Type Filters */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Contract Type</Label>
              <div className="space-y-2">
                {CONTRACT_TYPE_OPTIONS.map((option) => (
                  <div key={option.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={`type-${option.value}`}
                      checked={filters.contractType?.includes(option.value as ContractType) || false}
                      onCheckedChange={() => toggleContractType(option.value as ContractType)}
                    />
                    <label
                      htmlFor={`type-${option.value}`}
                      className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      {option.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

/**
 * Compact filters for mobile
 */
interface CompactFiltersProps {
  filters: ContractFilters;
  onFiltersChange: (updates: Partial<ContractFilters>) => void;
  className?: string;
}

export function CompactFilters({
  filters,
  onFiltersChange,
  className
}: CompactFiltersProps) {
  return (
    <div className={cn('flex gap-2', className)}>
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search..."
          value={filters.search || ''}
          onChange={(e) => onFiltersChange({ search: e.target.value })}
          className="pl-9"
        />
      </div>

      <Select
        value={filters.urgency?.[0] || 'all'}
        onValueChange={(value) => {
          if (value === 'all') {
            onFiltersChange({ urgency: [] });
          } else {
            onFiltersChange({ urgency: [value as ContractUrgency] });
          }
        }}
      >
        <SelectTrigger className="w-32">
          <SelectValue placeholder="Urgency" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All</SelectItem>
          {URGENCY_FILTER_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {URGENCY_LABELS[option.value as ContractUrgency]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
